import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage as FormikError } from "formik";
import * as Yup from "yup";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import i18n from "@/translate/i18n";
import { getPixAndGoStatus, completePixAndGo } from "@/services/pixAndGoApi";

function buildValidationSchema(requiredFields) {
  const shape = {};
  requiredFields.forEach((field) => {
    shape[field] = Yup.string().required(
      i18n.t("pixAndGo.form.fieldRequired", { field }),
    );
  });
  return Yup.object().shape(shape);
}

function buildInitialValues(requiredFields) {
  return requiredFields.reduce((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});
}

function PixAndGoForm() {
  const history = useHistory();
  const location = useLocation();
  const { uuid } = useParams();

  const merchantSlug = location.state?.merchantSlug;

  const [statusData, setStatusData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  useEffect(() => {
    getPixAndGoStatus(uuid)
      .then((res) => setStatusData(res.data))
      .catch(() => setLoadError(i18n.t("pixAndGo.form.loadError")));
  }, [uuid]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitError(null);
    try {
      const res = await completePixAndGo(uuid, values);
      const { status } = res.data;
      history.push({
        pathname: `/pix-and-go/${uuid}/processing`,
        state: { status, merchantSlug },
      });
    } catch (err) {
      setSubmitError(i18n.t("pixAndGo.form.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={3} />
      <main className="flex flex-col items-center flex-1 px-6 pb-10 sm:px-12">
        {children}
      </main>
    </PixAndGoLayout>
  );

  if (loadError) {
    return shell(<p className="text-red-500">{loadError}</p>);
  }

  if (!statusData) {
    return shell(
      <p className="text-pag-on-background-muted">
        {i18n.t("pixAndGo.form.loading")}
      </p>,
    );
  }

  const requiredFields = statusData.pending_fields || [];
  const validationSchema = buildValidationSchema(requiredFields);
  const initialValues = buildInitialValues(requiredFields);

  return shell(
    <div className="w-full max-w-xl flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-pag-on-background">
          {i18n.t("pixAndGo.form.title")}
        </h1>
        <p className="text-sm text-pag-on-background-muted">
          {i18n.t("pixAndGo.form.subtitle")}
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form className="flex flex-col gap-4">
            {requiredFields.map((field) => (
              <div key={field} className="flex flex-col gap-1.5">
                <label
                  htmlFor={field}
                  className="text-sm font-medium text-pag-on-background-muted"
                >
                  {i18n.t(`pixAndGo.form.fields.${field}`, {
                    defaultValue: field,
                  })}
                </label>
                <Field
                  id={field}
                  name={field}
                  type="text"
                  className="border border-gray-300 rounded-lg px-3.5 py-3.5 text-base outline-none focus:border-gray-400 w-full bg-transparent"
                  placeholder={i18n.t(`pixAndGo.form.placeholders.${field}`, {
                    defaultValue: "",
                  })}
                />
                <FormikError
                  name={field}
                  component="p"
                  className="text-xs text-red-500"
                />
              </div>
            ))}

            {submitError && (
              <p className="text-sm text-red-500 text-center">{submitError}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-full bg-pag-primary text-pag-on-primary font-semibold text-base disabled:opacity-60 mt-2"
            >
              {i18n.t("pixAndGo.form.submit")}
            </button>
          </Form>
        )}
      </Formik>
    </div>,
  );
}

export default PixAndGoForm;
